import React, { memo, useState, useCallback, useEffect } from 'react';
import { areEqual } from 'Utils/equalityChecks';
import { getPipelines, listProjectStageConfigs, parseApiError } from './actions';
import type { ProjectStageConfig, Pipeline, JobConfigProps } from './types';
import { JobConfigList } from './JobConfigList';
import { JobConfigForm } from './JobConfigForm';

import classes from './jobConfig.module.css';

function JobConfigContainer({ locationId, ssoToken }: JobConfigProps) {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [configs, setConfigs] = useState<ProjectStageConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPipeline, setSelectedPipeline] = useState<Pipeline | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [pipelinesData, configsData] = await Promise.all([
        getPipelines(locationId, ssoToken),
        listProjectStageConfigs(ssoToken),
      ]);
      setPipelines(pipelinesData);
      setConfigs(configsData);
    } catch (err) {
      setError(parseApiError(err));
      // eslint-disable-next-line no-console
      console.error('Error fetching job config data:', err);
    } finally {
      setLoading(false);
    }
  }, [ssoToken, locationId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSelectPipeline = useCallback((pipeline: Pipeline) => {
    setSelectedPipeline(pipeline);
  }, []);

  const handleBack = useCallback(() => {
    setSelectedPipeline(null);
    fetchData();
  }, [fetchData]);

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2 text-muted">Loading job configurations...</p>
      </div>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerRow}>
        <div>
          <h3 className="mb-1">Job Configuration</h3>
          <p className="text-muted mb-0">
            Configure automatic project creation when opportunities reach specific pipeline stages.
          </p>
        </div>
      </div>

      {error && (
        <div className="alert alert-danger mt-3" role="alert">
          {error}
        </div>
      )}

      {selectedPipeline ? (
        <JobConfigForm
          locationId={locationId}
          ssoToken={ssoToken}
          pipelineConfigs={configs.filter((c) => c.pipeline_id === selectedPipeline.id)}
          templateConfig={configs[0] || null}
          lockedPipeline={selectedPipeline}
          onSave={handleBack}
          onCancel={handleBack}
        />
      ) : (
        <>
          <JobConfigList
            pipelines={pipelines}
            configs={configs}
            onSelect={handleSelectPipeline}
          />
          <p className="text-muted mt-3" style={{ fontSize: '0.875rem' }}>
            If a contact has multiple jobs, consider using the opportunity address instead of the
            contact address. You can map opportunity address fields in each pipeline&apos;s field
            mapping configuration.
          </p>
        </>
      )}
    </div>
  );
}

const JobConfigContainerMemo = memo(JobConfigContainer, areEqual);

export { JobConfigContainerMemo as JobConfigContainer };
